import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { calculatePaymentStatus, getPaymentStatusText, getPaymentStatusBadgeClasses, validatePaymentAmount } from '../utils/paymentUtils';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { calculateTotalGSTBreakdown } from '../utils/gstUtils';
import TermsAndConditionsInput from './TermsAndConditionsInput';

const paymentOptions = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'phonepe', label: 'Phone Pay' },
  { value: 'google_pay', label: 'Google Pay' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'net_banking', label: 'NetBanking' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Others' }
];

const PaymentTypeSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // Max height of dropdown list is 240px
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // If there's less than 250px below, open upward
      const openUpward = spaceBelow < 250 && rect.top > spaceBelow;

      setMenuPos({
        top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          (!menuRef.current || !menuRef.current.contains(event.target))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const toggleDropdown = () => {
    if (!isOpen) {
      updatePosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const selectedOption = paymentOptions.find(o => o.value === value) || paymentOptions[0];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={toggleDropdown}
        className="w-full flex items-center justify-between pl-4 pr-10 py-2.5 bg-[#F8F5E6] border border-[#0A5F38]/20 rounded-lg text-[#0A5F38] font-bold text-left focus:ring-2 focus:ring-[#C5A57D] cursor-pointer hover:bg-[#F0EEE4] transition-colors"
      >
        <span>{selectedOption.label}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className={`w-4 h-4 text-[#0A5F38] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="z-[9999] bg-white border border-[#0A5F38]/10 rounded-lg shadow-xl max-h-60 overflow-y-auto py-1 animate-in fade-in duration-150 scrollbar-thin scrollbar-thumb-[#0A5F38] scrollbar-track-gray-100"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width
            }}
          >
            {paymentOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors ${
                    isSelected 
                      ? 'bg-[#0A5F38] text-white' 
                      : 'text-[#0A5F38] hover:bg-[#F8F5E6] hover:text-[#0A5F38]'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      }
    </div>
  );
};

const PaymentModal = ({
  isOpen,
  onClose,
  record,
  onPaymentUpdate,
  paymentHistory = [],
  companySettings = null,
  showTerms = false
}) => {
  const [includeTerms, setIncludeTerms] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_type: 'cash',
    other_payment_type: '',
    amount_received: '',
    notes: '',
    termsAndConditions: ''
  });
  const [loading, setLoading] = useState(false);

  const formatPaymentType = (type) => {
    if (!type) return 'Cash';
    const stringType = String(type).trim();
    const mappings = {
      'cash': 'Cash',
      'card': 'Card',
      'upi': 'UPI',
      'bank_transfer': 'Bank Transfer',
      'cheque': 'Cheque',
      'phonepe': 'Phone Pay',
      'google_pay': 'Google Pay',
      'paytm': 'Paytm',
      'net_banking': 'NetBanking'
    };
    return mappings[stringType.toLowerCase()] || stringType;
  };

  // Use modal scroll lock hook
  useModalScrollLock(isOpen);

  // Calculate amounts - handle potential string formatting issues
  const rawTotalAmount = record?.totalAmount || record?.total_amount || 0;
  const rawPaidAmount = record?.amountReceived || record?.amount_received || record?.paid_amount || record?.paidAmount || 0;

  // Parse amounts and handle comma-separated strings
  let totalAmount = typeof rawTotalAmount === 'string' ?
    parseFloat(rawTotalAmount.replace(/,/g, '')) : parseFloat(rawTotalAmount) || 0;
  // Calculate paid amount from history if available (more accurate), otherwise use record value
  let paidAmount = 0;
  if (paymentHistory && paymentHistory.length > 0) {
    paidAmount = paymentHistory.reduce((sum, payment) => {
      const amount = parseFloat(payment.amount || payment.amount_received || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    console.log('💰 Calculated paidAmount from history:', paidAmount);
  } else {
    // Fallback for legacy records or empty history
    paidAmount = typeof rawPaidAmount === 'string' ?
      parseFloat(rawPaidAmount.replace(/,/g, '')) : parseFloat(rawPaidAmount) || 0;
    console.log('💰 Used rawPaidAmount as fallback:', paidAmount);
  }

  // If totalAmount seems to be subtotal (less than expected), calculate grand total from items
  if (record?.items && record.items.length > 0) {
    // Calculate subtotal from items
    const subtotal = record.items.reduce((sum, item) => {
      const baseAmount = (item.quantity || 0) * (item.unit_price || item.unitPrice || 0);
      const discountAmount = baseAmount * ((item.discount || 0) / 100);
      return sum + (baseAmount - discountAmount);
    }, 0);

    // If stored totalAmount is less than subtotal, it's likely subtotal only - calculate grand total
    if (totalAmount < subtotal) {
      const gstBreakdown = calculateTotalGSTBreakdown(record.items, record.customer_gst || record.party_gst_no || '', companySettings);
      totalAmount = subtotal + gstBreakdown.totalGST;
      console.log('💰 Recalculated totalAmount from items:', { subtotal, gstTotal: gstBreakdown.totalGST, grandTotal: totalAmount });
    }
  }

  const remainingAmount = Math.max(0, totalAmount - paidAmount);
  const currentStatus = calculatePaymentStatus(paidAmount, totalAmount);
  const isPaid = currentStatus === 'paid';

  // Debug logging for amount calculations
  console.log('💰 PaymentModal Amount Debug:', {
    record,
    rawTotalAmount,
    rawPaidAmount,
    totalAmount,
    paidAmount,
    remainingAmount,
    totalAmountType: typeof totalAmount,
    paidAmountType: typeof paidAmount,
    remainingAmountType: typeof remainingAmount,
    rawTotalAmountType: typeof rawTotalAmount,
    rawPaidAmountType: typeof rawPaidAmount,
    recordFields: {
      totalAmount: record?.totalAmount,
      total_amount: record?.total_amount,
      amountReceived: record?.amountReceived,
      amount_received: record?.amount_received,
      paid_amount: record?.paid_amount
    }
  });

  useEffect(() => {
    if (isOpen) {
      setPaymentData({
        payment_type: 'cash',
        other_payment_type: '',
        amount_received: '',
        notes: '',
        termsAndConditions: record?.termsAndConditions || ''
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const paymentAmount = parseFloat(paymentData.amount_received) || 0;

    // Debug logging
    console.log('🔍 Payment Validation Debug:', {
      inputValue: paymentData.amount_received,
      paymentAmount,
      remainingAmount,
      totalAmount,
      paidAmount,
      comparison: `${paymentAmount} > ${remainingAmount} = ${paymentAmount > remainingAmount}`
    });

    const validation = validatePaymentAmount(paymentAmount, remainingAmount);

    if (!validation.isValid) {
      console.error('❌ Payment validation failed:', validation.error);
      toast.error(validation.error);
      return;
    }

    if (paymentData.payment_type === 'other' && !paymentData.other_payment_type.trim()) {
      toast.error('Please specify the payment type');
      return;
    }

    setLoading(true);
    try {
      const finalPaymentType = paymentData.payment_type === 'other'
        ? paymentData.other_payment_type.trim()
        : paymentData.payment_type;

      await onPaymentUpdate({
        ...paymentData,
        payment_type: finalPaymentType,
        amount_received: paymentAmount
      });
    } catch (error) {
      console.error('Payment update failed:', error);
    } finally {
      setLoading(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#F8F5E6] rounded-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

        {/* Integrated Header */}
        <div className="px-6 py-5 bg-white border-b border-[#0A5F38]/10 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-[#0A5F38] rounded-xl shadow-lg shadow-[#0A5F38]/20">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0A5F38] flex items-center gap-2">
                  Payment Details
                  <span className="text-sm font-medium px-2.5 py-0.5 bg-[#0A5F38]/10 rounded-full text-[#0A5F38]">
                    #{record?.documentNumber || record?.invoiceNumber || record?.orderNumber || record?.closed_order_number || record?.invoice_number || 'N/A'}
                  </span>
                </h2>
                <div className="flex items-center text-sm text-[#0A5F38]/70 mt-1 space-x-2">
                  <span className="font-medium">{record?.customerName || record?.customer_name || record?.customer?.name || 'Unknown Customer'}</span>
                  <span>•</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${currentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                    currentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                    {getPaymentStatusText(currentStatus)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-[#C5A57D] hover:text-[#B8985E] hover:bg-[#C5A57D]/10 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-[#0A5F38] scrollbar-track-gray-100">

          {/* Sleek Stats Strip */}
          <div className="flex items-center justify-between bg-white rounded-xl p-5 shadow-sm border border-[#0A5F38]/10 mb-8">
            <div className="flex flex-col flex-1 px-4 border-r border-[#0A5F38]/10 last:border-0">
              <span className="text-xs font-bold text-[#0A5F38]/60 uppercase tracking-wider mb-1">Total Amount</span>
              <span className="text-2xl font-bold text-[#0A5F38]">₹{totalAmount.toFixed(1)}</span>
            </div>
            <div className="flex flex-col flex-1 px-4 border-r border-[#0A5F38]/10 last:border-0">
              <span className="text-xs font-bold text-[#0A5F38]/60 uppercase tracking-wider mb-1">Paid</span>
              <span className="text-2xl font-bold text-[#0A5F38]">₹{paidAmount.toFixed(1)}</span>
            </div>
            <div className="flex flex-col flex-1 px-4">
              <span className="text-xs font-bold text-[#C5A57D] uppercase tracking-wider mb-1">Remaining</span>
              <span className="text-2xl font-bold text-[#C5A57D]">₹{remainingAmount.toFixed(1)}</span>
            </div>
          </div>

          {/* Payment Form */}
          {!isPaid && remainingAmount > 0 ? (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-[#0A5F38] mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                New Payment
              </h3>
              <form onSubmit={handleSubmit} className="bg-white p-5 rounded-xl border border-[#0A5F38]/10 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                  <div>
                    <label className="block text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider mb-2">Payment Type</label>
                    <div className="relative">
                      <PaymentTypeSelect
                        value={paymentData.payment_type}
                        onChange={(value) => setPaymentData({ ...paymentData, payment_type: value })}
                      />
                      {paymentData.payment_type === 'other' && (
                        <div className="mt-3">
                          <label className="block text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider mb-2">
                            Specify Payment Type
                          </label>
                          <input
                            type="text"
                            value={paymentData.other_payment_type}
                            onChange={(e) => setPaymentData({ ...paymentData, other_payment_type: e.target.value })}
                            className="w-full px-4 py-2.5 bg-[#F8F5E6] border-0 rounded-lg text-[#0A5F38] font-medium placeholder-[#0A5F38]/40 focus:ring-2 focus:ring-[#C5A57D] hover:bg-[#F0EEE4] transition-colors"
                            placeholder="Specify payment method..."
                            required
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider mb-2">
                      Amount (Max: ₹{remainingAmount.toFixed(1)})
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      value={paymentData.amount_received}
                      onChange={(e) => setPaymentData({ ...paymentData, amount_received: e.target.value })}
                      className="w-full px-4 py-2.5 bg-[#F8F5E6] border-0 rounded-lg text-[#0A5F38] font-medium placeholder-[#0A5F38]/40 focus:ring-2 focus:ring-[#C5A57D] hover:bg-[#F0EEE4] transition-colors"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {showTerms && (
                  <div className="mt-6 border-t border-[#0A5F38]/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-[#0A5F38]">Include Terms & Conditions</span>
                      <button
                        type="button"
                        onClick={() => {
                          const nextState = !includeTerms;
                          setIncludeTerms(nextState);
                          if (nextState) {
                            setPaymentData(prev => ({ ...prev, termsAndConditions: record?.termsAndConditions || '' }));
                          } else {
                            setPaymentData(prev => ({ ...prev, termsAndConditions: '' }));
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          includeTerms ? 'bg-[#0A5F38]' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            includeTerms ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {includeTerms && (
                      <TermsAndConditionsInput
                        value={paymentData.termsAndConditions}
                        onChange={(value) => setPaymentData({ ...paymentData, termsAndConditions: value })}
                        label="Terms and Conditions"
                      />
                    )}
                  </div>
                )}
              </form>
            </div>
          ) : (
            <div className="mb-8 p-5 bg-[#0A5F38]/5 border border-[#0A5F38]/10 rounded-xl flex items-center justify-center text-center">
              <div>
                <div className="inline-flex items-center justify-center p-2 bg-[#0A5F38]/10 rounded-full mb-2">
                  <CheckCircle className="h-6 w-6 text-[#0A5F38]" />
                </div>
                <h3 className="text-lg font-bold text-[#0A5F38]">Payment Completed</h3>
                <p className="text-[#0A5F38]/70 text-sm">This record has been fully paid.</p>
              </div>
            </div>
          )}

          {/* Refined Borderless History Table */}
          {paymentHistory && paymentHistory.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-[#0A5F38] mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                History
              </h3>
              <div className="bg-white rounded-xl border border-[#0A5F38]/10 shadow-sm overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-[#F8F5E6] text-left">
                      <th className="px-6 py-4 text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider">Mode</th>
                      <th className="px-6 py-4 text-xs font-bold text-[#0A5F38]/70 uppercase tracking-wider text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paymentHistory.map((payment, index) => (
                      <tr key={index} className="hover:bg-[#F8F5E6]/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-700">
                          {new Date(payment.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {formatPaymentType(payment.payment_type || payment.paymentType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-[#0A5F38] text-right">
                          ₹{(payment.amount || payment.amount_received || 0).toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Unified Actions Footer */}
        <div className="p-6 bg-white border-t border-[#0A5F38]/10 flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-bold text-[#0A5F38]/70 hover:text-[#0A5F38] hover:bg-[#0A5F38]/5 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {!isPaid && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !paymentData.amount_received}
              className="px-8 py-2.5 text-sm font-bold text-white bg-[#C5A57D] rounded-lg hover:bg-[#B8985E] shadow-lg shadow-[#C5A57D]/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Confirm Payment'
              )}
            </button>
          )}
        </div>

      </div>
    </div >
  );
};

export default PaymentModal;



